import { useState, useEffect } from 'react';
import { propertyAPI } from '../lib/property-api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Home, Send, Eye, CheckCircle, Clock, XCircle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Property } from '../types/property';
import { StockSharingFeed } from './StockSharingFeed';

interface ExternalAgentPortalProps {
  onBackToCatalog: () => void;
}

export function ExternalAgentPortal({ onBackToCatalog }: ExternalAgentPortalProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [inquiryForm, setInquiryForm] = useState({
    buyerName: '',
    buyerContact: '',
    buyerBudget: '',
    preApproved: false,
    message: '',
  });
  const [sharedProperties, setSharedProperties] = useState<Property[]>([]);
  const [myRequests] = useState<any[]>([]);

  // Load published listings for external agent view
  useEffect(() => {
    propertyAPI.getProperties({ pageSize: 8 })
      .then(({ listings }) => {
        // Adapt backend listing format to Property interface
        const adapted: Property[] = listings.map((l: any) => ({
          id: l.id || l.listingId || l.somethingPropertyId,
          slug: l.id || l.listingId,
          title: l.title || l.marketingHeading || 'Untitled',
          type: l.propertyType || 'House',
          status: 'Published' as const,
          address: {
            street: '',
            suburb: l.location?.split(',')[0]?.trim() || l.address?.split(',')[0]?.trim() || '',
            city: l.location?.split(',')[1]?.trim() || '',
            province: '',
            postalCode: '',
          },
          geo: { lat: 0, lng: 0 },
          price: l.price || l.listPrice || 0,
          facts: {
            bedrooms: l.bedrooms || 0,
            bathrooms: l.bathrooms || 0,
            parking: l.parking || 0,
          },
          amenities: l.features || [],
          media: (l.images || []).map((url: string) => ({ url, type: 'image' as const, isPrimary: false })),
          description: l.description || '',
          listingDate: l.createdAt ? new Date(l.createdAt).toISOString() : '',
          agentId: l.agentId || '',
          visibility: 'Public' as const,
          flags: {},
        }));
        setSharedProperties(adapted);
      })
      .catch(() => setSharedProperties([]));
  }, []);

  // Mock external agent info
  const externalAgent = {
    name: 'Jennifer Adams',
    company: 'Premium Properties SA',
    email: 'jennifer@premiumproperties.co.za',
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && password) {
      setIsAuthenticated(true);
      toast.success('Successfully logged in!');
    } else {
      toast.error('Please enter email and password');
    }
  };

  const handleSubmitInquiry = () => {
    if (!inquiryForm.buyerName || !inquiryForm.buyerContact || !inquiryForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    toast.success('Inquiry submitted successfully! The listing agent will contact you soon.');
    setSelectedProperty(null);
    setInquiryForm({
      buyerName: '',
      buyerContact: '',
      buyerBudget: '',
      preApproved: false,
      message: '',
    });
  };

  const getRequestStatusBadge = (status: string) => {
    const config: Record<string, { label: string; className: string; icon: any }> = {
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      'viewing-scheduled': { label: 'Viewing Scheduled', className: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      'offer-made': { label: 'Offer Made', className: 'bg-purple-100 text-purple-800', icon: CheckCircle },
      completed: { label: 'Completed', className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
      declined: { label: 'Declined', className: 'bg-red-100 text-red-800', icon: XCircle },
    };
    
    const Icon = config[status]?.icon || Clock;
    return (
      <Badge className={`${config[status]?.className} gap-1`}>
        <Icon className="h-3 w-3" />
        {config[status]?.label || status}
      </Badge>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>External Agent Portal</CardTitle>
            <CardDescription>
              Access shared listings and collaborate on deals
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <Button data-testid="external-agent-login-button" type="submit" className="w-full">
                Login
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                <p>Need access?</p>
                <Button data-testid="external-agent-request-account-button" variant="link" className="p-0 h-auto">
                  Request External Agent Account
                </Button>
              </div>

              <div className="pt-4 border-t">
                <Button
                  data-testid="external-agent-back-to-catalog-button"
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={onBackToCatalog}
                >
                  Back to Public Catalog
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="mb-2">External Agent Portal</h1>
          <p className="text-muted-foreground">
            Welcome back, {externalAgent.name} from {externalAgent.company}
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="external-agent-back-button" variant="outline" onClick={onBackToCatalog}>
            Back to Catalog
          </Button>
          <Button data-testid="external-agent-logout-button" onClick={() => {
            setIsAuthenticated(false);
            toast.info('Logged out successfully');
          }}>
            Logout
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Available Stock</CardDescription>
            <CardTitle>{sharedProperties.length}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Active Inquiries</CardDescription>
            <CardTitle className="text-yellow-600">
              {myRequests.filter(r => r.status === 'pending' || r.status === 'accepted').length}
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Completed Deals</CardDescription>
            <CardTitle className="text-green-600">
              {myRequests.filter(r => r.status === 'completed').length}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Tabs defaultValue="properties" className="space-y-6">
        <TabsList>
          <TabsTrigger value="properties">Available Properties</TabsTrigger>
          <TabsTrigger value="stock-sharing">Stock Sharing Feed</TabsTrigger>
        </TabsList>

        <TabsContent value="properties" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sharedProperties.map(property => (
              <Card key={property.id} className="overflow-hidden">
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={property.media[0]?.url}
                    alt={property.title}
                    className="object-cover w-full h-full"
                  />
                  <Badge className="absolute top-2 right-2">
                    For Sale
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="mb-2">{property.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    {property.address.suburb}, {property.address.city}
                  </p>

                  <div className="flex justify-between items-center mb-3">
                    <span className="font-semibold">R {property.price.toLocaleString()}</span>
                  </div>

                  <div className="flex gap-3 text-sm text-muted-foreground mb-4">
                    <span>{property.facts.bedrooms} Beds</span>
                    <span>{property.facts.bathrooms} Baths</span>
                    <span>{property.facts.parking} Parking</span>
                  </div>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        data-testid="external-agent-chat-button"
                        className="w-full"
                        onClick={() => setSelectedProperty(property)}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Chat on Stock Sharing
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Submit Buyer Inquiry</DialogTitle>
                        <DialogDescription>
                          {property.title} - {property.address.suburb}
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="buyerName">Buyer Name *</Label>
                            <Input
                              id="buyerName"
                              placeholder="John Smith"
                              value={inquiryForm.buyerName}
                              onChange={(e) => setInquiryForm({ ...inquiryForm, buyerName: e.target.value })}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="buyerContact">Contact Number *</Label>
                            <Input
                              id="buyerContact"
                              placeholder="+27 82 123 4567"
                              value={inquiryForm.buyerContact}
                              onChange={(e) => setInquiryForm({ ...inquiryForm, buyerContact: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="buyerBudget">Buyer Budget (Optional)</Label>
                          <Input
                            id="buyerBudget"
                            type="number"
                            placeholder="3500000"
                            value={inquiryForm.buyerBudget}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, buyerBudget: e.target.value })}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">Message to Listing Agent *</Label>
                          <Textarea
                            id="message"
                            placeholder="Provide details about your buyer and their requirements..."
                            rows={4}
                            value={inquiryForm.message}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="preApproved"
                            checked={inquiryForm.preApproved}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, preApproved: e.target.checked })}
                            className="rounded"
                          />
                          <Label htmlFor="preApproved" className="cursor-pointer">
                            Buyer is pre-approved
                          </Label>
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                          <Button data-testid="external-agent-inquiry-cancel-button" variant="outline" onClick={() => setSelectedProperty(null)}>
                            Cancel
                          </Button>
                          <Button data-testid="external-agent-inquiry-submit-button" onClick={handleSubmitInquiry}>
                            Submit Inquiry
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="stock-sharing">
          <StockSharingFeed 
            source="external"
            availableProperties={[]}
            onViewProperty={undefined}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}